import React, { useEffect, useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Upload,
  Form,
  Input,
  Space,
  Popconfirm,
  notificationProvider,
} from '@pankod/refine-antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';

const API_URL = process.env.REACT_APP_URL ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('konsole-token');
  return { authorization: token ? `Bearer ${token}` : '' };
}

interface TallyTcpFileRecord {
  id: string;
  fileVersion: string;
  fileName: string;
  isArchived: boolean;
  createdAt: string;
}

export const TallyTcpFilesPanel: React.FC = () => {
  const [records, setRecords] = useState<TallyTcpFileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileVersion, setFileVersion] = useState('');

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tally/tcp-file`, { headers: getAuthHeaders() });
      const data = await res.json();
      setRecords(Array.isArray(data) ? data : data?.data ?? []);
    } catch {
      notificationProvider.open({ type: 'error', message: 'Failed to load TCP files' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, []);

  const activeVersionId = records.find(r => !r.isArchived)?.id;
  const nonArchivedCount = records.filter(r => !r.isArchived).length;

  const handleUpload = async () => {
    if (!fileList[0] || !fileVersion.trim()) {
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileList[0] as RcFile);
    formData.append('fileVersion', fileVersion.trim());
    try {
      const res = await fetch(`${API_URL}/tally/tcp-file/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      notificationProvider.open({ type: 'success', message: 'Uploaded successfully' });
      setFileList([]);
      setFileVersion('');
      fetchRecords();
    } catch (err: any) {
      notificationProvider.open({ type: 'error', message: err?.message ?? 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/tally/tcp-file/${id}/archive`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body?.message ?? `HTTP ${res.status}`);
      }
      notificationProvider.open({ type: 'success', message: 'Version archived' });
      fetchRecords();
    } catch (err: any) {
      notificationProvider.open({ type: 'error', message: err?.message ?? 'Archive failed' });
    }
  };

  const columns = [
    { title: 'File Version', dataIndex: 'fileVersion', key: 'fileVersion' },
    { title: 'File Name', dataIndex: 'fileName', key: 'fileName' },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, r: TallyTcpFileRecord) => {
        if (r.isArchived) {
          return <Tag color="default">Archived</Tag>;
        }
        if (r.id === activeVersionId) {
          return <Tag color="green">Active</Tag>;
        }
        return <Tag color="blue">Inactive</Tag>;
      },
    },
    {
      title: 'Uploaded At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: TallyTcpFileRecord) => {
        if (r.isArchived) {
          return null;
        }
        const isOnlyNonArchived = nonArchivedCount <= 1;
        return (
          <Popconfirm
            title="Archive this version?"
            onConfirm={() => handleArchive(r.id)}
            disabled={isOnlyNonArchived}
          >
            <Button size="small" danger disabled={isOnlyNonArchived}>
              Archive
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item label="File Version">
          <Input
            value={fileVersion}
            onChange={e => setFileVersion(e.target.value)}
            placeholder="e.g. v1.01.21"
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item>
          <Upload
            beforeUpload={file => { setFileList([file]); return false; }}
            onRemove={() => setFileList([])}
            fileList={fileList}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Choose File</Button>
          </Upload>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={!fileList.length || !fileVersion.trim()}
          >
            Upload
          </Button>
        </Form.Item>
      </Form>
      <Table
        dataSource={records}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />
    </Space>
  );
};
